import styles from './styles/chart-overview.module.css';

import Loading from '../../common/Loading';
import {ReactComponent as ErrorIcon} from '../../../assets/dashboard/error.svg';
import {ReactComponent as ReloadIcon} from '../../../assets/dashboard/reload.svg';

import {Bar} from 'react-chartjs-2';
import {Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Tooltip, Legend} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Tooltip, Legend, ChartDataLabels);

export default function ChartOverview({ title, data, error, loading, handleReload }) {
    const processData = () => {
        if (!data) return { labels: null, counts: null, totalCount: 0 };
        
        const labels = data.map(item => item.name); // x축
        
        const formattedLabels = labels.map(label => { // 라벨 두 줄로 분리된 x축 (11자 이상인 경우)
            if (!label || label.length < 11) return label;
            const breakPoint = Math.ceil(label.length / 2);
            return [label.substring(0, breakPoint), label.substring(breakPoint)];
        });
        
        const counts = data.map(item => item.count); // y축

        const totalCount = data.reduce((acc, curr) => acc + curr.count, 0);
        
        return { labels, formattedLabels, counts, totalCount };
    };

    const { labels, formattedLabels, counts, totalCount } = processData();

    // 차트 스타일
    const color = title === '팀별 사용 현황' ? '#A79CFF' : '#825DFC';

    const labelCount = labels ? labels.length : 0;
    const needsScroll = labelCount > 10;

    const getCanvasWidth = () => { // 라벨(x축 데이터) 수에 따라 너비 계산하는 함수
        if (!needsScroll) return '100%';

        const itemWidth = 95; // 각 항목의 최소 너비(픽셀)
        return `${labelCount * itemWidth}px`;
    };

    const getMaxCount = (counts) => {
        if (!counts || counts.length === 0) return 10;
        return Math.max(...counts);
    };

    const formatNumber = (number) => {
        return new Intl.NumberFormat().format(number); // 숫자에 구분자(쉼표) 추가
    };

    const chartData = {
        labels: formattedLabels,
        datasets: [
            {
                label: '문서 검색 수',
                data: counts,
                backgroundColor: color,
                hoverBackgroundColor: color,
                borderRadius: 5
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            title: {
                display: false
            },
            datalabels: {
                display: false
            },
            tooltip: {
                position: 'nearest',
                padding: 10,
                backgroundColor: '#FFF',
                borderWidth: 1,
                borderColor: '#D9D7E3',
                titleColor: '#000',
                bodyColor: color,
                bodyFont: {
                    family: 'Pretendard, sans-serif'
                },
                titleFont: {
                    family: 'Pretendard, sans-serif'
                },
                boxPadding: 5,
                usePointStyle: true
            }
        },
        scales: {
            x: {
                stacked: true,
                grid: {
                    display: false
                },
                ticks: {
                    font: {
                        family: 'Pretendard, sans-serif'
                    },
                    minRotation: 0, // 라벨 회전 제거
                    maxRotation: 0, // 라벨 회전 제거
                    autoSkip: false // 라벨 건너뛰기 방지
                }
            },
            y: {
                stacked: true,
                grid: {
                    display: true
                },
                ticks: {
                    font: {
                        family: 'Pretendard, sans-serif'
                    },
                },
                min: 0,
                suggestedMax: Math.ceil(getMaxCount(counts) / 10) * 10 // 최댓값을 10의 배수로 올림
            }
        }
    };

    if (loading) {
        return (
            <div className={`${styles.wrapper} ${styles.loading}`}>
                {title === '팀별 사용 현황' ? <Loading /> : null}
            </div>
        );
    }

    if (!loading && data) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.containerTitle}>
                    <p>{title}</p>
                    <div className={styles.boxLegend}>
                        <p>총 {formatNumber(totalCount)}건</p>
                        <div className={styles.legend} style={{ width: 'fit-content' }}>
                            <div style={{ backgroundColor: color }}/>
                            <p>문서 검색 수</p>
                        </div>
                    </div>
                </div>
                <div className={styles.containerChart} datatype={needsScroll ? 'scroll' : ''}>
                    <div className={styles.boxCanvas} style={{ width: getCanvasWidth() }}>
                        <Bar data={chartData} options={options} />
                    </div>
                </div>
            </div>
        );
    }

    if (!loading && error) {
        return (
            <div className={`${styles.wrapper} ${styles.loading}`}>
                {title === '팀별 사용 현황'
                    ? <>
                        <ErrorIcon/>
                        <p className={styles.error}>
                            페이지를 불러오는 중 <span>오류</span> 발생
                        </p>
                        <button className={styles.btnReset} onClick={handleReload}>
                            재시도 <ReloadIcon/>
                        </button>
                    </>
                    : null
                }
            </div>
        );
    }
}
